export type ThediracResearchSite = {
  id: string
  category: 'prehistorie' | 'ijzertijd' | 'romeins' | 'middeleeuwen' | 'onbepaald'
  siteTypeNl: string
  siteTypeFr: string
  nameNl: string
  nameFr: string
  lon: number
  lat: number
  periodNl: string
  periodFr: string
  descriptionNl: string
  descriptionFr: string
  source: string
  sourceUrl?: string
  protected?: boolean
  locationQuality: 'exact' | 'source-centroid' | 'approximate'
  locationNoteNl?: string
  locationNoteFr?: string
}

// Detect toont ook beschermde monumenten en no-detectlocaties: ze horen bij
// het archeologische beeld. Een waarschuwing verbergt dus nooit een punt.
// Coördinaten worden evenmin verschoven of kunstmatig vervaagd.
//
// 'exact': de openbare bron publiceert het punt als locatie van het object.
// 'source-centroid': openbaar centrum van het genoemde lieu-dit/complex.
// 'approximate': alleen een globale bronpositie of gemeentecentrum is bekend.
export const THEDIRAC_RESEARCH_SITES: ThediracResearchSite[] = [
  {
    id: 'le-garrisset',
    category: 'prehistorie',
    siteTypeNl: 'Openluchtvindplaats / bewerkingszone',
    siteTypeFr: 'Site de plein air / zone de débitage',
    nameNl: 'Le Garisset / Garrisset',
    nameFr: 'Le Garisset / Garrisset',
    lon: 1.351786,
    lat: 44.610608,
    periodNl: 'Oud- en Midden-Paleolithicum',
    periodFr: 'Paléolithique inférieur et moyen',
    descriptionNl: 'Diagnostiek in de groevezone Peyrilles–Lavercantière–Thédirac leverde meer dan 200 stenen artefacten op, vrijwel volledig van lokaal kwarts en metaquartziet. Een deel lag verplaatst in solifluctielagen: sterke activiteitenzone, maar geen bewezen intacte nederzettingsvloer.',
    descriptionFr: 'Les diagnostics de la zone de carrière Peyrilles–Lavercantière–Thédirac ont livré plus de 200 artefacts, presque tous en quartz ou métaquartzite local. Une partie est remaniée dans des coulées de solifluxion : zone d’activité certaine, mais pas sol d’habitat intact démontré.',
    source: 'Inrap / FranceArchives — diagnostic Le Garisset; positie: IGN-toponiem',
    sourceUrl: 'https://francearchives.gouv.fr/facomponent/e97db9770da13528d0eb7a276334743cddc58ec3',
    locationQuality: 'source-centroid',
    locationNoteNl: 'Marker op het openbare IGN-toponiem; het onderzochte gebied omvat meerdere groevepercelen.',
    locationNoteFr: 'Marqueur placé sur le toponyme IGN public ; l’emprise diagnostiquée couvre plusieurs parcelles de carrière.'
  },
  {
    id: 'surges-dolmen',
    category: 'prehistorie',
    siteTypeNl: 'Grafmonument',
    siteTypeFr: 'Monument funéraire',
    nameNl: 'Dolmen van Surgès',
    nameFr: 'Dolmen de Surgès',
    lon: 1.30557920416873,
    lat: 44.6208014204898,
    periodNl: 'Neolithicum – Chalcolithicum',
    periodFr: 'Néolithique – Chalcolithique',
    descriptionNl: 'Uitzonderlijke Quercy-dolmen met zij-ingang, aan het oostelijke uiteinde van een cairn van circa 30 meter. Het monument is gedurende meerdere millennia aangepast en hergebruikt.',
    descriptionFr: 'Dolmen exceptionnel du Quercy à entrée latérale, situé à l’extrémité orientale d’un cairn d’environ 30 mètres. Le monument a été remanié et réutilisé pendant plusieurs millénaires.',
    source: 'Ministère de la Culture — POP / Mérimée PA46000063',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA46000063',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'riffat-i',
    category: 'prehistorie',
    siteTypeNl: 'Grafmonument',
    siteTypeFr: 'Monument funéraire',
    nameNl: 'Dolmen du Riffat I',
    nameFr: 'Dolmen du Riffat I',
    lon: 1.3167,
    lat: 44.5833,
    periodNl: 'Laat-Neolithicum / Chalcolithicum',
    periodFr: 'Néolithique final / Chalcolithique',
    descriptionNl: 'Menselijk bot uit deze context is gedateerd op circa 2869–2491 v.Chr. Gemelde vondsten zijn onder meer een vuurstenen pijlpunt en klingetje, twee jadeïetkralen en een benen speldkop.',
    descriptionFr: 'Un os humain de ce contexte est daté d’environ 2869–2491 av. J.-C. Le mobilier signalé comprend une pointe et une lamelle en silex, deux perles en jadéite et une tête d’épingle en os.',
    source: 'Radiocarbon / archeologische literatuur — Riffat I',
    sourceUrl: 'https://repository.arizona.edu/bitstream/handle/10150/655416/18000-27550-1-PB.pdf',
    protected: true,
    locationQuality: 'approximate',
    locationNoteNl: 'Gepubliceerde globale kaartpositie; niet als exact monumentpunt gebruiken.',
    locationNoteFr: 'Position cartographique globale publiée ; ne pas l’interpréter comme l’emplacement exact du monument.'
  },
  {
    id: 'courtis-menhir',
    category: 'prehistorie',
    siteTypeNl: 'Staand megalithisch monument',
    siteTypeFr: 'Monument mégalithique dressé',
    nameNl: 'Menhir van Courtis',
    nameFr: 'Menhir de Courtis',
    lon: 1.30316,
    lat: 44.56906,
    periodNl: 'Neolithicum',
    periodFr: 'Néolithique',
    descriptionNl: 'Door Jean Clottes geïnventariseerde menhir bij Montgesty. Het punt is opgenomen als monumentale landschapsreferentie, niet als bewijs voor een nederzetting.',
    descriptionFr: 'Menhir de Montgesty inventorié par Jean Clottes. Le point sert de repère monumental dans le paysage et ne constitue pas une preuve d’habitat.',
    source: 'Jean Clottes, Inventaire des mégalithes de la France — Lot',
    sourceUrl: 'https://www.persee.fr/doc/galip_0072-0100_1977_sup_1_5',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'peyrefiche',
    category: 'prehistorie',
    siteTypeNl: 'Megalithische structuur',
    siteTypeFr: 'Structure mégalithique',
    nameNl: 'Peyrefiche — twee staande platen',
    nameFr: 'Peyrefiche — deux dalles plantées',
    lon: 1.284011,
    lat: 44.605087,
    periodNl: 'Waarschijnlijk Neolithicum / Bronstijd',
    periodFr: 'Probablement Néolithique / âge du Bronze',
    descriptionNl: 'Clottes beschrijft aan de rand van een combe twee rechtopstaande platen. Dit is een concrete megalithische aanwijzing, maar geen aangetoonde woonplaats.',
    descriptionFr: 'Clottes décrit deux dalles plantées au bord d’une combe. Il s’agit d’un indice mégalithique concret, mais pas d’un habitat démontré.',
    source: 'Jean Clottes, Inventaire des mégalithes; positie: IGN-toponiem Peyrefiche',
    sourceUrl: 'https://www.persee.fr/doc/galip_0072-0100_1977_sup_1_5',
    protected: true,
    locationQuality: 'source-centroid'
  },
  {
    id: 'mas-cascavel-champ-bout',
    category: 'prehistorie',
    siteTypeNl: 'Oppervlaktevindplaats',
    siteTypeFr: 'Station de surface',
    nameNl: 'Mas de Cascavel / Champ de Bout',
    nameFr: 'Mas de Cascavel / Champ de Bout',
    lon: 1.283075,
    lat: 44.582484,
    periodNl: 'Prehistorie',
    periodFr: 'Préhistoire',
    descriptionNl: 'Literatuur noemt bij Mas de Cascavel onder meer een pijlpunt en twee gitkralen; Champ de Bout wordt als oppervlaktevindplaats beschreven. De exacte begrenzing van de vondstspreiding is niet gepubliceerd.',
    descriptionFr: 'La littérature signale notamment une pointe et deux perles en jais au Mas de Cascavel ; Champ de Bout est décrit comme station de surface. L’étendue exacte de la dispersion n’est pas publiée.',
    source: 'Inventaris Jean Clottes / Préhistoire du Sud-Ouest; positie: IGN-toponiem Cascavel',
    sourceUrl: 'https://www.persee.fr/doc/galip_0072-0100_1977_sup_1_5',
    locationQuality: 'source-centroid'
  },
  {
    id: 'gindou-paleolithic-unlocated',
    category: 'prehistorie',
    siteTypeNl: 'Gemelde vindplaats — exacte plek onbekend',
    siteTypeFr: 'Site signalé — emplacement exact inconnu',
    nameNl: 'Gindou — laat-paleolithische vondsten',
    nameFr: 'Gindou — découvertes du Paléolithique supérieur',
    lon: 1.2627,
    lat: 44.6236,
    periodNl: 'Laat-Paleolithicum; mogelijk latere bijmenging',
    periodFr: 'Paléolithique supérieur ; mélange plus récent possible',
    descriptionNl: 'Een lokale publicatie meldt klingetjes, schrabber, stekerfragment, dierlijk bot en handgevormd aardewerk. Zonder openbaar onderzoeksrapport of lieu-dit blijft de precieze vindplaats onopgelost.',
    descriptionFr: 'Une publication locale signale lamelles, grattoir, fragment de burin, restes animaux et céramique modelée. Sans rapport public ni lieu-dit, l’emplacement précis reste inconnu.',
    source: 'Lokale geschiedschrijving Gindou — nog zonder teruggevonden primair rapport',
    sourceUrl: 'https://www.charles-de-flahaut.fr/wordpress/histoire-et-patrimoine-de-gindou-lot/',
    locationQuality: 'approximate',
    locationNoteNl: 'Marker staat op het openbare gemeentecentrum van Gindou, uitsluitend om de melding in het regionale beeld te bewaren.',
    locationNoteFr: 'Marqueur placé au centre public de la commune de Gindou, uniquement pour conserver le signalement dans la vue régionale.'
  },
  {
    id: 'mas-de-rieu',
    category: 'romeins',
    siteTypeNl: 'Landelijke bewoning / villa-indicatie',
    siteTypeFr: 'Habitat rural / indice de villa',
    nameNl: 'Mas de Rieu',
    nameFr: 'Mas de Rieu',
    lon: 1.311259,
    lat: 44.570694,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'In 1971 vond hier een reddingsopgraving plaats. De locatie wordt in regionale archeologische overzichten verbonden met resten van een Gallo-Romeinse villa of landelijke bewoning.',
    descriptionFr: 'Une fouille de sauvetage y a été menée en 1971. Les synthèses régionales associent le lieu à des vestiges de villa ou d’habitat rural gallo-romain.',
    source: 'Frantiq — rapport de fouille de sauvetage René Pauc; positie: IGN-toponiem',
    sourceUrl: 'https://catalogue.frantiq.fr/bib/661676',
    locationQuality: 'source-centroid',
    locationNoteNl: 'Toponiemcentrum van Mas de Rieu, niet het gepubliceerde opgravingsvierkant.',
    locationNoteFr: 'Centre du toponyme Mas de Rieu, et non carré de fouille publié.'
  },
  {
    id: 'montgesty-combes',
    category: 'romeins',
    siteTypeNl: 'Materiaalvondst',
    siteTypeFr: 'Découverte de mobilier',
    nameNl: 'Chemin des Combes — Romeinse tegels',
    nameFr: 'Chemin des Combes — tuiles romaines',
    lon: 1.294418,
    lat: 44.580959,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'Michel Labrousse meldt hier Romeinse randtegels. Dat bewijst antieke activiteit in de omgeving, maar op zichzelf nog geen afzonderlijke villa.',
    descriptionFr: 'Michel Labrousse y signale des tuiles romaines à rebord. Elles prouvent une activité antique dans le secteur, sans démontrer à elles seules une villa distincte.',
    source: 'Gallia 1962; positie: huidige openbare wegcentrum Route des Combes',
    sourceUrl: 'https://www.persee.fr/doc/galia_0016-4119_1962_num_20_2_2372',
    locationQuality: 'source-centroid'
  },
  {
    id: 'montgesty-lapeze-calfour',
    category: 'romeins',
    siteTypeNl: 'Materiaalvondst',
    siteTypeFr: 'Découverte de mobilier',
    nameNl: 'Lapèze–Calfour — Romeinse tegels',
    nameFr: 'Lapèze–Calfour — tuiles romaines',
    lon: 1.278491,
    lat: 44.581863,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'Gallia noemt Romeinse randtegels bij Lapèze–Calfour. De marker gebruikt het openbare centrum van de huidige Route de Lapeze.',
    descriptionFr: 'Gallia mentionne des tuiles romaines à rebord à Lapèze–Calfour. Le marqueur utilise le centre public de l’actuelle Route de Lapeze.',
    source: 'Gallia 1962; positie: openbare wegcentrum Route de Lapeze',
    sourceUrl: 'https://www.persee.fr/doc/galia_0016-4119_1962_num_20_2_2372',
    locationQuality: 'source-centroid'
  },
  {
    id: 'maussac-roman-material',
    category: 'romeins',
    siteTypeNl: 'Materiaalconcentratie / bewoningsindicatie',
    siteTypeFr: 'Concentration de mobilier / indice d’habitat',
    nameNl: 'Maussac — Romeinse vondsten',
    nameFr: 'Maussac — découvertes romaines',
    lon: 1.276932,
    lat: 44.599251,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'Oudere archeologische literatuur noemt bij Maussac randtegels, keizerlijke munten en terra sigillata. Het materiaal wijst op Romeinse activiteit of bewoning in deze sector.',
    descriptionFr: 'La littérature archéologique ancienne signale à Maussac des tuiles à rebord, des monnaies impériales et de la sigillée, indiquant une activité ou un habitat romain dans ce secteur.',
    source: 'A. Viré, 1940; positie: IGN-toponiem Maussac',
    sourceUrl: 'https://www.jstor.org/stable/41751007',
    locationQuality: 'source-centroid'
  },
  {
    id: 'le-frau-tile-workshops',
    category: 'romeins',
    siteTypeNl: 'Productieplaats bouwkeramiek',
    siteTypeFr: 'Atelier de terres cuites architecturales',
    nameNl: 'Le Frau / Le Grand Frau',
    nameFr: 'Le Frau / Le Grand Frau',
    lon: 1.353832,
    lat: 44.630395,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'Wetenschappelijke inventarisaties registreren in het grensgebied Lavercantière–Thédirac productie van tegulae en andere bouwkeramiek. Productielandschap, niet automatisch woonnederzetting.',
    descriptionFr: 'Les inventaires scientifiques enregistrent dans le secteur Lavercantière–Thédirac une production de tegulae et autres terres cuites architecturales. Paysage de production, pas automatiquement habitat.',
    source: 'Revue archéologique du Centre de la France; positie: IGN-toponiem le Frau',
    sourceUrl: 'https://journals.openedition.org/racf/1809',
    locationQuality: 'source-centroid'
  },
  {
    id: 'vayrieres-tile-workshop',
    category: 'romeins',
    siteTypeNl: 'Productieplaats bouwkeramiek',
    siteTypeFr: 'Atelier de terres cuites architecturales',
    nameNl: 'Vayrières — tegelproductie',
    nameFr: 'Vayrières — production de tuiles',
    lon: 1.348822,
    lat: 44.617363,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'Geregistreerde productieplaats voor Romeinse bouwkeramiek; er zijn ook stempels gemeld. De marker staat op het openbare toponiem, niet op een afzonderlijke oven.',
    descriptionFr: 'Site enregistré de production de terres cuites architecturales romaines, avec estampilles signalées. Le marqueur correspond au toponyme public, pas à un four isolé.',
    source: 'Revue archéologique du Centre de la France; positie: IGN-toponiem Vayrières',
    sourceUrl: 'https://journals.openedition.org/racf/1809',
    locationQuality: 'source-centroid'
  },
  {
    id: 'camp-bouxe-sagnes',
    category: 'romeins',
    siteTypeNl: 'Opgegraven tegelmakersatelier',
    siteTypeFr: 'Atelier de tuilier sondé',
    nameNl: 'Camp de Bouxe / Les Sagnes',
    nameFr: 'Camp de Bouxe / Les Sagnes',
    lon: 1.391976,
    lat: 44.572415,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'Bij de Sagnes-beek in Saint-Denis-Catus werd in 1981 een tegelmakersatelier onderzocht, met onder meer een uit tegulae opgebouwde muur en brand-/aslagen.',
    descriptionFr: 'Au bord du ruisseau des Sagnes à Saint-Denis-Catus, un atelier de tuilier a été sondé en 1981, avec notamment un mur construit en tegulae et des niveaux de combustion/cendres.',
    source: 'Gallia 1983; positie: openbaar lieu-ditcentrum Les Sagnes',
    sourceUrl: 'https://www.persee.fr/doc/galia_0016-4119_1983_num_41_2_1904',
    locationQuality: 'source-centroid'
  },
  {
    id: 'uzech-les-sagnes',
    category: 'romeins',
    siteTypeNl: 'Geregistreerde oppervlaktewerkplaats',
    siteTypeFr: 'Atelier de surface inventorié',
    nameNl: 'Uzech — Les Sagnes',
    nameFr: 'Uzech — Les Sagnes',
    lon: 1.395445,
    lat: 44.590341,
    periodNl: 'Romeinse tijd',
    periodFr: 'Époque romaine',
    descriptionNl: 'De academische productie-inventaris registreert Les Sagnes bij Uzech als oppervlaktewerkplaats voor tegels en mogelijk hypocaustelementen. Geen bewijs voor bewoning.',
    descriptionFr: 'L’inventaire académique enregistre Les Sagnes à Uzech comme atelier de surface produisant des tuiles et peut-être des éléments d’hypocauste. Ce n’est pas une preuve d’habitat.',
    source: 'Revue archéologique du Centre de la France — Filippini 2010',
    sourceUrl: 'https://journals.openedition.org/racf/1809',
    locationQuality: 'approximate',
    locationNoteNl: 'Exact lieu-ditpunt niet teruggevonden; marker staat op het openbare centrum van Uzech.',
    locationNoteFr: 'Le point exact du lieu-dit n’a pas été retrouvé ; marqueur placé au centre public d’Uzech.'
  },
  {
    id: 'catus-priory-occupation',
    category: 'romeins',
    siteTypeNl: 'Meerperiodenbewoning en priorij',
    siteTypeFr: 'Occupation multipériode et prieuré',
    nameNl: 'Catus — priorij Saint-Jean',
    nameFr: 'Catus — prieuré Saint-Jean',
    lon: 1.3357429225344,
    lat: 44.5561087975241,
    periodNl: 'Romeins 1e–4e eeuw; middeleeuwen en later',
    periodFr: 'Antiquité Ier–IVe s. ; Moyen Âge et périodes postérieures',
    descriptionNl: 'Opgravingen vonden Romeins bouwmateriaal en aardewerk onder vrijwel het gehele priorijterrein. De middeleeuwse priorij werd rond 1385–1405 grotendeels verlaten; tevens zijn circa veertig begravingen aangetroffen.',
    descriptionFr: 'Les fouilles ont trouvé matériaux de construction et céramiques antiques sous presque toute l’emprise du prieuré. Le prieuré médiéval fut en grande partie abandonné vers 1385–1405 ; environ quarante sépultures ont aussi été mises au jour.',
    source: 'HADÈS Archéologie / POP PA00095053',
    sourceUrl: 'https://www.hades-archeologie.com/operation/prieure-saint-jean/',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'degagnazes-priory',
    category: 'middeleeuwen',
    siteTypeNl: 'Verlaten kloostercomplex',
    siteTypeFr: 'Ensemble monastique abandonné',
    nameNl: 'Priorij van Dégagnazès',
    nameFr: 'Prieuré de Dégagnazès',
    lon: 1.360477,
    lat: 44.610603,
    periodNl: 'Middeleeuwen – Nieuwe tijd',
    periodFr: 'Moyen Âge – époque moderne',
    descriptionNl: 'Na een schenking in 1235 ontstond hier een Grandmontaanse priorij. In 1679 was de kerk gedeeltelijk ruïneus en waren de conventsgebouwen al verwoest.',
    descriptionFr: 'Un prieuré grandmontain fut fondé après une donation de 1235. En 1679, l’église était en partie ruinée et les bâtiments conventuels déjà détruits.',
    source: 'Inventaire général du patrimoine culturel — IA46100305; positie: IGN-toponiem',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/IA46100305',
    protected: true,
    locationQuality: 'source-centroid'
  },
  {
    id: 'uzech-chateau',
    category: 'middeleeuwen',
    siteTypeNl: 'Kasteelruïne / versterkte bewoning',
    siteTypeFr: 'Ruines castrales / habitat fortifié',
    nameNl: 'Kasteelruïne van Uzech',
    nameFr: 'Ruines du château d’Uzech',
    lon: 1.393338,
    lat: 44.589972,
    periodNl: 'Middeleeuwen, vanaf ten minste 14e eeuw',
    periodFr: 'Moyen Âge, au moins depuis le XIVe siècle',
    descriptionNl: 'Gedocumenteerde resten van het kasteel, mogelijk in de 15e eeuw Château de Pechbernat genoemd. De erfgoedinventaris geeft oude percelen D1 116–120 en moderne percelen D 308, 311–315 en 926.',
    descriptionFr: 'Vestiges documentés du château, peut-être appelé Château de Pechbernat au XVe siècle. L’inventaire cite les anciennes parcelles D1 116–120 et les parcelles actuelles D 308, 311–315 et 926.',
    source: 'Inventaire général du patrimoine culturel — IA46100281; positie: IGN-toponiem le Château',
    sourceUrl: 'https://inventaire.patrimoines.laregion.fr/dossier/IA46100281',
    protected: true,
    locationQuality: 'source-centroid'
  },
  {
    id: 'uzech-medieval-house',
    category: 'middeleeuwen',
    siteTypeNl: 'Landelijke woning',
    siteTypeFr: 'Maison rurale',
    nameNl: 'Uzech — huis uit de 13e eeuw',
    nameFr: 'Uzech — maison du XIIIe siècle',
    lon: 1.395429,
    lat: 44.591124,
    periodNl: 'Circa 1205–1225',
    periodFr: 'Vers 1205–1225',
    descriptionNl: 'Zeldzame bewaarde landelijke middeleeuwse woning buiten de dorpskern, door archeologisch onderzoek gedateerd tussen 1205 en 1225. Het gebouw is eigendom van het departement.',
    descriptionFr: 'Rare maison rurale médiévale conservée hors du centre du village, datée par étude archéologique entre 1205 et 1225. Le bâtiment appartient au Département.',
    source: 'Gemeente Uzech / Département du Lot; positie: openbare Rue Maison XIIIe',
    sourceUrl: 'https://www.uzech-les-oules.fr/decouvrir-uzech',
    protected: true,
    locationQuality: 'source-centroid'
  },
  {
    id: 'thedirac-chateau',
    category: 'middeleeuwen',
    siteTypeNl: 'Kasteel / historische bewoning',
    siteTypeFr: 'Château / habitat historique',
    nameNl: 'Kasteel van Thédirac',
    nameFr: 'Château de Thédirac',
    lon: 1.3183,
    lat: 44.6017,
    periodNl: 'Middeleeuwen – vooral eind 15e eeuw',
    periodFr: 'Moyen Âge – principalement fin du XVe siècle',
    descriptionNl: 'Het inventarisdossier noemt de familie Thédirac al in de jaren 1150. Het huidige kasteel bevat vooral laatmiddeleeuwse bouwfasen.',
    descriptionFr: 'Le dossier d’inventaire mentionne les Thédirac dès les années 1150. Le château actuel conserve surtout des phases de construction de la fin du Moyen Âge.',
    source: 'Région Occitanie / Département du Lot — POP IA46101577',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/IA46101577',
    protected: true,
    locationQuality: 'source-centroid'
  },
  {
    id: 'uzech-coin-hoard',
    category: 'ijzertijd',
    siteTypeNl: 'Munthoard — exacte vindplek onbekend',
    siteTypeFr: 'Trésor monétaire — emplacement exact inconnu',
    nameNl: 'Keltische muntschat van Uzech',
    nameFr: 'Trésor monétaire d’Uzech',
    lon: 1.395445,
    lat: 44.590341,
    periodNl: 'Late IJzertijd / Keltische periode',
    periodFr: 'Second âge du Fer / période celtique',
    descriptionNl: 'In het najaar van 1880 werd in Uzech een muntschat gevonden met sterk vervormde imitaties van drachmen van Rhodè. De precieze historische vindplaats is niet teruggevonden.',
    descriptionFr: 'À l’automne 1880, un trésor contenant des imitations très déformées de drachmes de Rhodè fut découvert à Uzech. L’emplacement historique précis n’a pas été retrouvé.',
    source: 'Historische numismatische literatuur; positie: gemeentecentrum Uzech',
    sourceUrl: 'https://publicacions.iec.cat/repository/pdf/00000311/00000038.pdf',
    locationQuality: 'approximate',
    locationNoteNl: 'Gemeentecentrum; de marker beweert nadrukkelijk niet de exacte vindplek te zijn.',
    locationNoteFr: 'Centre de la commune ; le marqueur ne prétend pas représenter le lieu exact de découverte.'
  },
  {
    id: 'les-ruffes-iron-slag',
    category: 'onbepaald',
    siteTypeNl: 'Metaalproductiespoor',
    siteTypeFr: 'Indice de métallurgie',
    nameNl: 'Les Ruffes — ijzerslakken',
    nameFr: 'Les Ruffes — scories de fer',
    lon: 1.312879,
    lat: 44.611332,
    periodNl: 'Onbekend',
    periodFr: 'Indéterminée',
    descriptionNl: 'Gallia meldt op dit lieu-dit ijzerslakken. Dat is een concreet spoor van ijzerbewerking, maar de bron dateert het materiaal niet; dus niet automatisch Romeins.',
    descriptionFr: 'Gallia signale des scories de fer à ce lieu-dit. Il s’agit d’un indice concret de métallurgie, mais la source ne date pas le matériel ; il ne faut donc pas l’attribuer automatiquement à l’époque romaine.',
    source: 'Gallia 1962; positie: IGN-toponiem les Ruffes',
    sourceUrl: 'https://www.persee.fr/doc/galia_0016-4119_1962_num_20_2_2372',
    locationQuality: 'source-centroid'
  },
  {
    id: 'le-piage',
    category: 'prehistorie',
    siteTypeNl: 'Grot-/abri-afzetting en opgegraven bewoningsreeks',
    siteTypeFr: 'Gisement en grotte/abri et séquence d’occupations fouillée',
    nameNl: 'Le Piage',
    nameFr: 'Le Piage',
    lon: 1.36515,
    lat: 44.797351,
    periodNl: 'Midden- en Laat-Paleolithicum, circa 40.000–30.000 v.Chr.',
    periodFr: 'Paléolithique moyen et supérieur, environ 40 000–30 000 av. J.-C.',
    descriptionNl: 'De afzetting bewaart opeenvolgende bewoningsfasen van de laatste Neanderthalers en de eerste moderne mensen in West-Europa. Na onderzoek in de jaren 1960 zijn vanaf 2004 nieuwe opgravingen uitgevoerd door een internationaal team.',
    descriptionFr: 'Le gisement conserve des occupations successives des derniers Néandertaliens et des premiers humains modernes d’Europe occidentale. Après les recherches des années 1960, de nouvelles fouilles sont menées depuis 2004 par une équipe internationale.',
    source: 'CC Quercy Bouriane / SRA Occitanie — Le Piage; positie: openbaar sitepunt',
    sourceUrl: 'https://ccqb.fr/economie-tourisme/piage-prehistoire/',
    locationQuality: 'exact'
  },
  {
    id: 'cougnac-cave',
    category: 'prehistorie',
    siteTypeNl: 'Grot met prehistorische schilderingen',
    siteTypeFr: 'Grotte ornée de peintures préhistoriques',
    nameNl: 'Grot van Cougnac',
    nameFr: 'Grotte de Cougnac',
    lon: 1.37353384327964,
    lat: 44.7585419432009,
    periodNl: 'Prehistorie',
    periodFr: 'Préhistoire',
    descriptionNl: 'De officiële monumentenregistratie beschermt hier de prehistorisch beschilderde grotwand op de percelen B 716 en 796.',
    descriptionFr: 'La notice des Monuments historiques protège ici la paroi ornée de peintures préhistoriques sur les parcelles B 716 et 796.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095181',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095181',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'roc-de-la-cave',
    category: 'prehistorie',
    siteTypeNl: 'Beschermde archeologische afzetting',
    siteTypeFr: 'Gisement archéologique protégé',
    nameNl: 'Roc de la Cave',
    nameFr: 'Gisement du Roc de la Cave',
    lon: 1.32228222071089,
    lat: 44.792600453457105,
    periodNl: 'Prehistorie',
    periodFr: 'Préhistoire',
    descriptionNl: 'Prehistorische vindplaats op perceel D 30 bij Saint-Cirq-Madelon, sinds 23 december 1929 als historisch monument beschermd.',
    descriptionFr: 'Gisement préhistorique de la parcelle D 30 à Saint-Cirq-Madelon, classé Monument historique depuis le 23 décembre 1929.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095238',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095238',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'combe-negre-caves',
    category: 'prehistorie',
    siteTypeNl: 'Archeologisch grottencomplex',
    siteTypeFr: 'Ensemble archéologique de grottes',
    nameNl: 'Grotten van Combe Nègre',
    nameFr: 'Grottes de Combe Nègre',
    lon: 1.13569869473175,
    lat: 44.5873247184998,
    periodNl: 'Paleolithicum',
    periodFr: 'Paléolithique',
    descriptionNl: 'Paleolithische versierde grot. De bescherming omvat de bodem en ondergrond van perceel A 651 en alle holtes die daaronder liggen.',
    descriptionFr: 'Grotte ornée paléolithique. La protection couvre le sol et le sous-sol de la parcelle A 651 ainsi que toutes les cavités situées en dessous.',
    source: 'Ministère de la Culture — POP / Mérimée PA46000043',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA46000043',
    protected: true,
    locationQuality: 'source-centroid',
    locationNoteNl: 'Officieel bronpunt voor het beschermde perceel en het onderliggende grottencomplex; geen begrenzing van iedere afzonderlijke holte.',
    locationNoteFr: 'Point officiel représentant la parcelle protégée et les cavités sous-jacentes ; il ne délimite pas chaque cavité.'
  },
  {
    id: 'ginouillac-cloups',
    category: 'prehistorie',
    siteTypeNl: 'Geknikte dolmen / grafmonument',
    siteTypeFr: 'Dolmen coudé / monument funéraire',
    nameNl: 'Dolmen des Cloups',
    nameFr: 'Dolmen des Cloups',
    lon: 1.53656978751022,
    lat: 44.7238925111659,
    periodNl: 'Neolithicum',
    periodFr: 'Néolithique',
    descriptionNl: 'Dolmen aan de rand van een combe. Clottes beschrijft een kleine, hoge kamer met kalkstenen steunen en een zware tafel en ingangplaat van ijzerhoudende zandsteen.',
    descriptionFr: 'Dolmen au bord d’une combe. Clottes décrit une petite chambre haute, avec supports calcaires et une lourde table ainsi qu’une dalle d’entrée en grès sidérolithique.',
    source: 'Ministère de la Culture — POP PA00095097; Jean Clottes, inventaire du Lot',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095097',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'soucirac-fosses',
    category: 'prehistorie',
    siteTypeNl: 'Dolmen / grafmonument',
    siteTypeFr: 'Dolmen / monument funéraire',
    nameNl: 'Dolmen des Fosses',
    nameFr: 'Dolmen des Fosses',
    lon: 1.49338260461957,
    lat: 44.7027398385767,
    periodNl: 'Prehistorie, vermoedelijk Neolithicum',
    periodFr: 'Préhistoire, probablement Néolithique',
    descriptionNl: 'Als historisch monument beschermde dolmen op perceel A 496 bij Soucirac. De officiële registratie geeft geen nadere datering.',
    descriptionFr: 'Dolmen classé Monument historique sur la parcelle A 496 à Soucirac. La notice officielle ne fournit pas de datation plus précise.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095261',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095261',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'prayssac-bertrandoune',
    category: 'prehistorie',
    siteTypeNl: 'Dolmen / grafmonument',
    siteTypeFr: 'Dolmen / monument funéraire',
    nameNl: 'Dolmen de la Bertrandoune',
    nameFr: 'Dolmen de la Bertrandoune',
    lon: 1.21857460102987,
    lat: 44.5283594430749,
    periodNl: 'Neolithicum',
    periodFr: 'Néolithique',
    descriptionNl: 'Neolithische dolmen op het lieu-dit Le Causse bij Prayssac, sinds 1988 als historisch monument geregistreerd.',
    descriptionFr: 'Dolmen néolithique au lieu-dit Le Causse à Prayssac, inscrit au titre des Monuments historiques depuis 1988.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095184',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095184',
    protected: true,
    locationQuality: 'exact'
  },
  {
    id: 'gourdon-costeraste',
    category: 'prehistorie',
    siteTypeNl: 'Dolmen / grafmonument',
    siteTypeFr: 'Dolmen / monument funéraire',
    nameNl: 'Dolmen de Costeraste',
    nameFr: 'Dolmen de Costeraste',
    lon: 1.34358,
    lat: 44.70604,
    periodNl: 'Neolithicum',
    periodFr: 'Néolithique',
    descriptionNl: 'Uitzonderlijke dolmen in de Céou-vallei, enkele tientallen meters van de rivier. Clottes mat een circa 4,70 × 4,00 meter grote deksteen van ongeveer 40 ton; één zijsteun ontbreekt.',
    descriptionFr: 'Dolmen exceptionnel dans la vallée du Céou, à quelques dizaines de mètres de la rivière. Clottes mesure une table d’environ 4,70 × 4,00 mètres, proche de 40 tonnes ; un support latéral manque.',
    source: 'Jean Clottes, Inventaire des mégalithes de la France — Lot; positie: openbaar OSM-monumentpunt',
    sourceUrl: 'https://www.persee.fr/doc/galip_0072-0100_1977_sup_1_5',
    locationQuality: 'exact'
  },
  {
    id: 'salviac-pech-curet',
    category: 'prehistorie',
    siteTypeNl: 'Opgegraven dolmen / grafmonument',
    siteTypeFr: 'Dolmen fouillé / monument funéraire',
    nameNl: 'Dolmen du Pech Curet',
    nameFr: 'Dolmen du Pech Curet',
    lon: 1.28697,
    lat: 44.69591,
    periodNl: 'Neolithicum',
    periodFr: 'Néolithique',
    descriptionNl: 'Dolmen aan de rand van de causse. Een reddingsonderzoek in 1972 documenteerde onder meer negen dwarspijlen, vuursteen, kwarts-klopstenen, een maalsteenfragment, een benen priem en verbrande menselijke botfragmenten.',
    descriptionFr: 'Dolmen en bordure du causse. Une fouille de sauvetage en 1972 a documenté notamment neuf flèches tranchantes, du silex, des percuteurs en quartz, un fragment de meule, un poinçon en os et des os humains brûlés.',
    source: 'Jean Clottes, Inventaire des mégalithes de la France — Lot; positie: openbaar OSM-monumentpunt',
    sourceUrl: 'https://www.persee.fr/doc/galip_0072-0100_1977_sup_1_5',
    locationQuality: 'exact'
  },
  {
    id: 'saint-projet-rigou',
    category: 'prehistorie',
    siteTypeNl: 'Dolmen / grafmonument',
    siteTypeFr: 'Dolmen / monument funéraire',
    nameNl: 'Dolmen de Rigou',
    nameFr: 'Dolmen de Rigou',
    lon: 1.49786,
    lat: 44.75804,
    periodNl: 'Neolithicum',
    periodFr: 'Néolithique',
    descriptionNl: 'Massieve dolmen op een pech bij Saint-Projet, gebouwd met dikke platen van ijzerhoudende zandsteen. Clottes mat een deksteen van circa 2,80 × 2,10 meter; een herkenbare tumulus ontbrak.',
    descriptionFr: 'Dolmen massif sur un pech à Saint-Projet, construit avec d’épaisses dalles de grès sidérolithique. Clottes mesure une table d’environ 2,80 × 2,10 mètres ; aucun tumulus n’était visible.',
    source: 'Jean Clottes, Inventaire des mégalithes de la France — Lot; positie: openbaar OSM-monumentpunt',
    sourceUrl: 'https://www.persee.fr/doc/galip_0072-0100_1977_sup_1_5',
    locationQuality: 'exact'
  },
  {
    id: 'luzech-impernal',
    category: 'ijzertijd',
    siteTypeNl: 'Oppidum / versterkte nederzetting',
    siteTypeFr: 'Oppidum / habitat fortifié',
    nameNl: 'Oppidum van l’Impernal',
    nameFr: 'Oppidum de l’Impernal',
    lon: 1.28242958642632,
    lat: 44.4876500367207,
    periodNl: 'Late IJzertijd en Gallo-Romeinse tijd',
    periodFr: 'Second âge du Fer et époque gallo-romaine',
    descriptionNl: 'Beschermde delen van een versterkte nederzetting op de Pech de l’Impernal. De officiële registratie omvat meerdere percelen en dateert de plek in de late IJzertijd en Gallo-Romeinse periode.',
    descriptionFr: 'Parties protégées d’un habitat fortifié sur le Pech de l’Impernal. La notice officielle couvre plusieurs parcelles et date le site du second âge du Fer et de l’époque gallo-romaine.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095154',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095154',
    protected: true,
    locationQuality: 'source-centroid',
    locationNoteNl: 'Officieel POP-punt voor een oppidum dat over meerdere beschermde percelen loopt.',
    locationNoteFr: 'Point POP officiel représentant un oppidum réparti sur plusieurs parcelles protégées.'
  },
  {
    id: 'murcens-cras-oppidum',
    category: 'ijzertijd',
    siteTypeNl: 'Oppidum / georganiseerde versterkte bewoning',
    siteTypeFr: 'Oppidum / habitat fortifié organisé',
    nameNl: 'Oppidum van Murcens-Cras',
    nameFr: 'Oppidum de Murcens-Cras',
    lon: 1.5573992,
    lat: 44.5501803,
    periodNl: 'Late IJzertijd',
    periodFr: 'Fin de l’âge du Fer',
    descriptionNl: 'Groot versterkt complex met het zuidelijkst bekende voorbeeld van een murus gallicus. Opgravingen in 1984–1991 bevestigden de wal, gelijktijdige en oudere bewoning en zones met metaalbewerking.',
    descriptionFr: 'Grand ensemble fortifié avec le murus gallicus le plus méridional connu. Les fouilles de 1984 à 1991 ont confirmé le rempart, des habitats contemporains et plus anciens ainsi que des zones de métallurgie.',
    source: 'ADLFI / SRA Occitanie — Mercadier & Büchsenschütz; positie: openbaar complexpunt',
    sourceUrl: 'https://journals.openedition.org/adlfi/10392',
    locationQuality: 'source-centroid',
    locationNoteNl: 'Marker vertegenwoordigt het centrum van het uitgestrekte oppidumcomplex, niet één afzonderlijke vondstplek.',
    locationNoteFr: 'Le marqueur représente le centre du vaste oppidum, et non un locus de découverte isolé.'
  },
  {
    id: 'vers-roman-aqueduct',
    category: 'romeins',
    siteTypeNl: 'Aquaduct / Romeinse infrastructuur',
    siteTypeFr: 'Aqueduc / infrastructure antique',
    nameNl: 'Gallo-Romeins aquaduct van Vers',
    nameFr: 'Aqueduc gallo-romain de Vers',
    lon: 1.5390521571132902,
    lat: 44.4708132588182,
    periodNl: 'Gallo-Romeinse tijd',
    periodFr: 'Époque gallo-romaine',
    descriptionNl: 'Officieel geregistreerd traject van een Gallo-Romeins aquaduct. De bescherming noemt meerdere percelen in de secties B en E.',
    descriptionFr: 'Tracé officiellement inscrit d’un aqueduc gallo-romain. La protection mentionne plusieurs parcelles dans les sections B et E.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095280',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095280',
    protected: true,
    locationQuality: 'source-centroid',
    locationNoteNl: 'Het officiële POP-punt vertegenwoordigt een lijnvormig monument over meerdere percelen; het is niet de volledige loop.',
    locationNoteFr: 'Le point POP officiel représente un monument linéaire réparti sur plusieurs parcelles ; ce n’est pas son tracé complet.'
  },
  {
    id: 'cazals-castrum',
    category: 'middeleeuwen',
    siteTypeNl: 'Castrum / versterkte bewoningskern',
    siteTypeFr: 'Castrum / noyau d’habitat fortifié',
    nameNl: 'Castrum van Cazals',
    nameFr: 'Castrum de Cazals',
    lon: 1.22871881379966,
    lat: 44.6445688375139,
    periodNl: 'Vanaf de 12e eeuw',
    periodFr: 'À partir du XIIe siècle',
    descriptionNl: 'In de 12e eeuw gesticht castrum. Het beschermde geheel omvat de verdedigingsstructuren en de onderliggende bodem; een romaanse toren bleef in een later herbouwd huis bewaard.',
    descriptionFr: 'Castrum fondé au XIIe siècle. L’ensemble protégé comprend les défenses et leurs sols ; une tour romane subsiste dans une maison reconstruite plus tard.',
    source: 'Ministère de la Culture — POP / Mérimée PA00132875',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00132875',
    protected: true,
    locationQuality: 'source-centroid',
    locationNoteNl: 'Officieel bronpunt van het beschermde ensemble op de percelen AB 30–35.',
    locationNoteFr: 'Point officiel de l’ensemble protégé sur les parcelles AB 30 à 35.'
  },
  {
    id: 'leobard-abbaye-nouvelle',
    category: 'middeleeuwen',
    siteTypeNl: 'Verlaten abdij en ruïnegrond',
    siteTypeFr: 'Abbaye abandonnée et sols de ruines',
    nameNl: 'Abbaye Nouvelle',
    nameFr: 'Abbaye Nouvelle',
    lon: 1.30794595501153,
    lat: 44.7087950021571,
    periodNl: '13e–20e eeuw; grotendeels verlaten vanaf de 17e eeuw',
    periodFr: 'XIIIe–XXe siècles ; largement abandonnée dès le XVIIe siècle',
    descriptionNl: 'Cisterciënzerabdij gebouwd omstreeks 1260–1280. Halverwege de 17e eeuw woonden er geen religieuzen meer; in 1777 lagen de conventsgebouwen in puin en later werd het complex als steengroeve gebruikt.',
    descriptionFr: 'Abbaye cistercienne bâtie vers 1260–1280. Au milieu du XVIIe siècle, aucun religieux n’y résidait plus ; en 1777 les bâtiments conventuels étaient en ruine, puis le site servit de carrière de pierre.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095134',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095134',
    protected: true,
    locationQuality: 'source-centroid',
    locationNoteNl: 'Officieel centrum van het beschermde complex; de bescherming omvat ook bodem en ondergrond van percelen C 354–355.',
    locationNoteFr: 'Centre officiel de l’ensemble protégé ; la protection couvre aussi le sol et le sous-sol des parcelles C 354–355.'
  },
  {
    id: 'luzech-old-castle-enclosure',
    category: 'middeleeuwen',
    siteTypeNl: 'Toren, omwalling en begraven kasteelresten',
    siteTypeFr: 'Tour, enceinte et vestiges castraux enfouis',
    nameNl: 'Oud kasteel van Luzech',
    nameFr: 'Ancien château de Luzech',
    lon: 1.28634288113324,
    lat: 44.4799224767522,
    periodNl: '13e eeuw',
    periodFr: 'XIIIe siècle',
    descriptionNl: 'De bescherming omvat niet alleen de 13e-eeuwse toren, maar ook bovengrondse en begraven resten en de bodem en ondergrond van de omwalling.',
    descriptionFr: 'La protection couvre non seulement la tour du XIIIe siècle, mais aussi les vestiges en élévation et enfouis ainsi que le sol et le sous-sol de l’enceinte.',
    source: 'Ministère de la Culture — POP / Mérimée PA00095151',
    sourceUrl: 'https://pop.culture.gouv.fr/notice/merimee/PA00095151',
    protected: true,
    locationQuality: 'source-centroid',
    locationNoteNl: 'Officieel punt van het beschermde kasteelensemble; de resten beslaan meerdere percelen.',
    locationNoteFr: 'Point officiel de l’ensemble castral protégé ; les vestiges couvrent plusieurs parcelles.'
  },
  {
    id: 'grotte-roucadour',
    category: 'prehistorie',
    siteTypeNl: 'Versierde grot en bewoningscontext',
    siteTypeFr: 'Grotte ornée et contexte d’occupation',
    nameNl: 'Grotte de Roucadour',
    nameFr: 'Grotte de Roucadour',
    lon: 1.815295,
    lat: 44.721176,
    periodNl: 'Paleolithicum tot Neolithicum; versieringen vooral Gravettien',
    periodFr: 'Paléolithique au Néolithique ; art principalement gravettien',
    descriptionNl: 'Belangrijk maar niet bezoekbaar grotheiligdom met 495 geregistreerde motieven, waaronder 139 dieren zoals paarden, katachtigen, reuzenherten, bizons en mammoeten. Latere bewoning uit het Mesolithicum en Neolithicum is eveneens aangetoond. De grot is afgesloten voor conservering: niet naar de ingang gaan, niet betreden en niet detecteren.',
    descriptionFr: 'Important sanctuaire souterrain non visitable comptant 495 motifs recensés, dont 139 animaux : chevaux, félins, mégacéros, bisons et mammouths. Des occupations mésolithiques et néolithiques sont aussi attestées. La grotte est fermée pour sa conservation : ne pas chercher l’entrée, ne pas pénétrer et ne pas prospecter.',
    source: 'Ministère de la Culture — Grands sites archéologiques / Roucadour',
    sourceUrl: 'https://archeologie.culture.gouv.fr/fr/roucadour',
    protected: true,
    locationQuality: 'exact',
    locationNoteNl: 'Openbaar gepubliceerd monumentpunt; geen bezoekersingang en geen toestemming om het terrein te betreden.',
    locationNoteFr: 'Point monumental publié ; ni entrée de visite ni autorisation d’accéder au terrain.'
  },
  {
    id: 'archeosite-fieux',
    category: 'prehistorie',
    siteTypeNl: 'Prehistorische grotval en archeosite',
    siteTypeFr: 'Piège naturel préhistorique et archéosite',
    nameNl: 'Archéosite des Fieux',
    nameFr: 'Archéosite des Fieux',
    lon: 1.68822526,
    lat: 44.8767191,
    periodNl: 'Midden-Paleolithicum tot recente prehistorie; circa 80.000 jaar gebruik',
    periodFr: 'Paléolithique moyen à la Préhistoire récente ; environ 80 000 ans d’utilisation',
    descriptionNl: 'Een natuurlijke holte die bijna 80.000 jaar als dierenval en jachtplaats is gebruikt. De archeosite presenteert ook een versierde grot via beeldmateriaal en activiteiten. De publieksstatus verandert: controleer de officiële heropening vóór vertrek. Buiten het bezoekersparcours geldt beschermd/no-detectterrein.',
    descriptionFr: 'Cavité naturelle utilisée pendant près de 80 000 ans comme piège à animaux et lieu de chasse. L’archéosite présente aussi une grotte ornée par images et animations. Le statut d’ouverture évolue : vérifier la réouverture officielle avant le départ. Hors parcours, site protégé et sans prospection.',
    source: 'Parc naturel régional des Causses du Quercy — Archéosite des Fieux',
    sourceUrl: 'https://www.parc-causses-du-quercy.fr/fiche-tourisme/archeosite-des-fieux-prehistoire/',
    protected: true,
    locationQuality: 'exact',
    locationNoteNl: 'Officieel bezoekerspunt; opening vooraf controleren en uitsluitend het toegestane parcours volgen.',
    locationNoteFr: 'Point d’accueil officiel ; vérifier l’ouverture et rester exclusivement sur le parcours autorisé.'
  }
]

export const THEDIRAC_RESEARCH_LAYER_NAME = `Archeologische plekken · regio Thédirac (${THEDIRAC_RESEARCH_SITES.length})`
